import type { StateStorage } from "zustand/middleware";

// Keep navigation working when storage is blocked or full. Never fall back to URLs.
export function createSafeSessionStorage(getStorage: () => StateStorage): StateStorage {
	const memory = new Map<string, string>();
	let unavailable = false;
	return {
		getItem(name) {
			if (unavailable) return memory.get(name) ?? null;
			try {
				const value = getStorage().getItem(name);
				if (typeof value === "string") {
					try {
						JSON.parse(value);
					} catch {
						memory.delete(name);
						getStorage().removeItem(name);
						return null;
					}
					memory.set(name, value);
				} else if (value === null) {
					memory.delete(name);
				}
				return value;
			} catch {
				unavailable = true;
				return memory.get(name) ?? null;
			}
		},
		setItem(name, value) {
			memory.set(name, value);
			if (!unavailable) {
				try {
					getStorage().setItem(name, value);
				} catch {
					unavailable = true;
					// A quota error must not leave an older property to restore on refresh.
					try {
						getStorage().removeItem(name);
					} catch {
						/* Storage is blocked. */
					}
				}
			}
		},
		removeItem(name) {
			memory.delete(name);
			try {
				getStorage().removeItem(name);
			} catch {
				unavailable = true;
			}
		},
	};
}

export const journeyStorage = createSafeSessionStorage(() => window.sessionStorage);
export const journeyStorageKey = (
	name: string,
	site = process.env.NEXT_PUBLIC_SITE || "Solstice",
) => `${name}:${site}`;
